import FeedCard from '@/components/dashboard/Feed/FeedCard';
import { addPostUrl, departmentOptions, getPostsUrl, businessAdsUrl, DepartmentEnum } from '@/constants';
import { useAppContext } from '@/context/context';
import { Post } from '@sm-dashboard/types';
import instanceAxios from '@/util/axios';
import { EditorComponent } from '@sm-dashboard/form-provider';
import {
  Button as UiButton,
  IconButton as UiIconButton,
  Select as UiSelect,
  Loader,
  Icon,
  Dialog
} from '@sm-dashboard/ui-components';
import { Add, Remove } from '@mui/icons-material';
import { Box, Stack, Tooltip, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useParams, useSearchParams } from 'react-router-dom';
import { Filters } from '@sm-dashboard/grid-data';
import { FilterConfig } from '@sm-dashboard/types';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import FeedTagsAllow from '@/components/dashboard/Feed/FeedTags';
import { BusinessAdsCarousel } from '@/components/dashboard/Feed/BusinessAdsCarousel/BusinessAdsCarousel';
import { BusinessAd } from '@/components/dashboard/BusinessAds/businessAdsUtils';
import { FeedBannersCarousel } from '@/components/dashboard/Feed/FeedBannersCarousel';
import { FeedBannersDialog } from '@/components/dashboard/Feed/FeedBannersDialog';
import { AddPostMobileSheet } from '@/components/dashboard/Feed/AddPostMobileSheet';

export function FeedContainer({ isPage, onReady }: { isPage: boolean; onReady?: () => void }) {
  const initialPostString = 'פרסם כאן משהו לכל השלוחים';
  const [searchParams, setSearchParams] = useSearchParams();
  const [posts, setPosts] = useState<Post[]>([]);
  const [editContent, setEditContent] = useState<string | undefined>(initialPostString);
  const [loading, setLoading] = useState(false);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [department, setDepartment] = useState<DepartmentEnum | undefined>(undefined);
  const [expanded, setExpanded] = useState(false);
  const [pagesCount, setPagesCount] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>('');
  const [categories, setCategories] = useState<string[]>([]);
  const { postId } = useParams();
  const postRefs = useRef<(HTMLDivElement | null)[]>([]);
  const { setSnackbar, userPermission } = useAppContext();
  const canManagePermissions = Boolean(
    userPermission?.isAdmin || userPermission?.isSuperAdmin || userPermission?.hasTag('עורך חדשות')
  );
  const canManageBanners = Boolean(userPermission?.isAdmin || userPermission?.isSuperAdmin);
  const defaultDepartment = canManagePermissions ? DepartmentEnum.EZREI_SHLICHUT : undefined;
  const currentPage = parseInt(searchParams.get('page') || '0');
  const hasMorePages = pagesCount > currentPage + 1;
  const [tagsAllow, setTagsAllow] = useState<string[]>([]);
  const [openTagsDialog, setOpenTagsDialog] = useState(false);
  const [openBannersDialog, setOpenBannersDialog] = useState(false);
  const [bannersRefreshKey, setBannersRefreshKey] = useState(0);
  const [businessAds, setBusinessAds] = useState<BusinessAd[]>([]);
  const hasNotifiedReady = useRef(false);

  const [filterConfigs, setFilterConfigs] = useState<FilterConfig<Post>[]>([]);

  const departmentSelectOptions = useMemo(
    () => departmentOptions.map(option => ({ label: option.label, value: option.value })),
    []
  );

  const fetchNextData = () => {
    const next = new URLSearchParams(searchParams);
    next.set('page', String(currentPage + 1));
    setSearchParams(next);
  };

  const fetchPosts = useCallback(async () => {
    setLoadingSearch(true);
    try {
      const queryParams = searchParams;
      if (currentPage !== 0 && !(posts.length > 0)) {
        queryParams.delete('page');
        setSearchParams(queryParams);
        setLoadingSearch(false);
        return;
      }

      const response = await instanceAxios.get(`${getPostsUrl}/?${queryParams.toString()}`);
      const rows = response?.data?.rows || [];

      if (currentPage === 0) {
        setPosts(rows);
      } else {
        setPosts(prevPosts => [...prevPosts, ...rows]);
      }

      if (response?.data?.categories && response.data.categories.length > 0) {
        setCategories(response.data.categories);
      }
      setPagesCount(Math.ceil(response?.data?.count / 15) || 1);
      setLoadingSearch(false);
      if (onReady && !hasNotifiedReady.current && currentPage === 0) {
        hasNotifiedReady.current = true;
        onReady();
      }
    } catch (error) {
      setLoadingSearch(false);
      console.error('Error fetching posts:', error);
      if (onReady && !hasNotifiedReady.current && currentPage === 0) {
        hasNotifiedReady.current = true;
        onReady();
      }
    }
  }, [searchParams, onReady, currentPage]);

  const refreshFeed = () => fetchPosts();

  const resetFeed = () => {
    const params = new URLSearchParams();
    params.set('page', '0');
    setSearchParams(params);
  };

  const fetchBusinessAds = useCallback(async () => {
    try {
      const randomSeed = Math.floor(Math.random() * 1000000);
      const response = await instanceAxios.get(`${businessAdsUrl}?status=approved&randomSeed=${randomSeed}`);
      const allAds = response?.data?.rows || [];
      setBusinessAds(allAds.slice(0, 3));
    } catch (error) {
      console.error('Error fetching business ads:', error);
      setBusinessAds([]);
    }
  }, []);

  useEffect(() => {
    fetchBusinessAds();
  }, [fetchBusinessAds]);

  const addPost = async () => {
    try {
      setLoading(true);
      await instanceAxios.post(addPostUrl, {
        content: editContent,
        department,
        category: selectedCategory,
        tagsAllow: tagsAllow
      });
      resetFeed();

      setLoading(false);
      handleCleanPost();
      closeAddPostDialog();
      setSnackbar({ message: 'פוסט נוסף בהצלחה', severity: 'success', open: true });
    } catch (error) {
      setLoading(false);
      setSnackbar({ message: 'תקלה בשמירת פוסט', severity: 'error', open: true });
    }
  };

  useEffect(() => {
    fetchPosts();
  }, [searchParams, fetchPosts]);

  useEffect(() => {
    if (postId && postRefs.current[Number(postId)]) {
      setTimeout(() => {
        postRefs.current[Number(postId)]?.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    }
  }, [postId, posts]);

  const handleCleanPost = () => {
    setDepartment(defaultDepartment);
    setEditContent('');
    setTagsAllow([]);
    setSelectedCategory('');
  };

  const handleSaveTags = async (selectedTags: string[]) => {
    setTagsAllow(selectedTags);
    setOpenTagsDialog(false);
    return Promise.resolve();
  };

  useEffect(() => {
    // if (categories.length > 0) {
    const configs: FilterConfig<Post>[] = [
      {
        field: 'searchTerm' as any,
        label: 'חיפוש',
        type: 'text',
        show: true,
        level: 'primary'
      },
      {
        field: 'department',
        label: 'אגף',
        type: 'select',
        options: departmentOptions.map(option => ({ name: option.label, value: option.value })),
        level: 'regular'
      }
    ];
    setFilterConfigs(configs);
    // }
  }, [categories, userPermission]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const openAddPostDialog = () => {
    setDepartment(defaultDepartment);
    setExpanded(true);
  };
  const closeAddPostDialog = () => setExpanded(false);
  const isPostDirty =
    Boolean(editContent) && editContent !== initialPostString && editContent !== `<p>${initialPostString}</p>`;

  const addPostIconButton = (
    <Tooltip title={expanded ? 'סגור' : 'הוסף פוסט'}>
      <UiIconButton
        color="warning"
        variant="contained"
        shape="circle"
        size="medium"
        onClick={() => (expanded ? closeAddPostDialog() : openAddPostDialog())}
        aria-label={expanded ? 'סגור' : 'הוסף פוסט'}
      >
        {expanded ? <Remove /> : <Add />}
      </UiIconButton>
    </Tooltip>
  );

  const addPostTextButton = (
    <Stack
      direction="row"
      spacing={1}
      sx={{
        flexWrap: 'nowrap',
        '& .MuiButton-root': {
          width: 'auto',
          flexShrink: 0,
          whiteSpace: 'nowrap'
        }
      }}
    >
      <UiButton
        onClick={openAddPostDialog}
        variant="contained"
        color="primary"
        size="medium"
        fullWidth={false}
        endIcon={<Add fontSize="small" />}
      >
        הוסף פוסט
      </UiButton>
      {canManageBanners && (
        <UiButton
          onClick={() => setOpenBannersDialog(true)}
          variant="outlined"
          color="primary"
          size="medium"
          fullWidth={false}
          endIcon={<Icon icon="image" color="inherit" size="small" />}
        >
          הוסף/ערוך באנרים
        </UiButton>
      )}
    </Stack>
  );

  const addPostForm = (
    <>
      {(userPermission?.isAdmin || userPermission?.isSuperAdmin || userPermission.hasTag('עורך חדשות')) && (
        <Box sx={{ pt: 0.5, pb: isMobile ? 1 : 0.5 }}>
          <UiSelect
            key={expanded ? `department-${department ?? 'empty'}` : 'department-closed'}
            label="אגף"
            appearance="plain"
            value={department}
            onChange={value => setDepartment(value as DepartmentEnum | undefined)}
            options={departmentSelectOptions}
            fullWidth
          />
        </Box>
      )}

      {canManagePermissions && (
        <Box sx={{ mt: isMobile ? 2 : 1, mb: isMobile ? 2 : 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
          <UiButton
            onClick={() => setOpenTagsDialog(true)}
            variant="outlined"
            size="small"
            fullWidth={false}
            startIcon={tagsAllow.length > 0 ? <LockIcon /> : <LockOpenIcon />}
          >
            {tagsAllow.length > 0 ? `הרשאות צפייה (${tagsAllow.length})` : 'הגדרת הרשאות צפייה'}
          </UiButton>

          {tagsAllow.length > 0 && (
            <Tooltip title={`הרשאות נדרשות: ${tagsAllow.join(', ')}`}>
              <UiIconButton
                onClick={() => setOpenTagsDialog(true)}
                variant="text"
                color="primary"
                size="small"
                aria-label="הרשאות צפייה"
              >
                <LockIcon fontSize="small" />
              </UiIconButton>
            </Tooltip>
          )}
        </Box>
      )}

      <Box
        sx={{
          width: '100%',
          maxWidth: '100%',
          overflow: 'hidden',
          '& .tox-tinymce': { maxWidth: '100%' },
          '& .tox-menubar': { display: { xs: 'none', md: 'flex' } },
          '& .tox-promotion': { display: 'none' },
          '& .tox-statusbar__branding': { display: 'none' }
        }}
      >
        <EditorComponent height={isMobile ? 260 : 400} value={editContent} setValue={setEditContent} />
      </Box>
    </>
  );

  return (
    <>
      <Box
        id="scrollableDiv"
        sx={{
          pt: { xs: 0, sm: 2, md: 4 },
          pb: { xs: 0, sm: 2, md: 4 },
          pl: { xs: 0, md: '5px' },
          pr: { xs: 0, md: 'unset' },
          width: '100%',
          maxWidth: { xs: '100%', md: '700px' },
          margin: 'auto',
          // Flex fill (not only height:100%) so the infinite-scroll target gets a real
          // scrollport in iOS WKWebView inside the dashboard content column.
          flex: 1,
          minHeight: 0,
          height: '100%',
          overflow: 'auto',
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: { xs: 'none', md: 'auto' },
          msOverflowStyle: { xs: 'none', md: 'auto' },
          '::-webkit-scrollbar': {
            width: { xs: '0', md: '0.4em' }
          },
          '::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,.1)'
          },
          '& > div': {
            width: '100%',
            maxWidth: { xs: '100%', md: '800px' },
            margin: '0 auto',
            padding: { xs: 0, md: 2 }
          }
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            width: '100%',
            p: isPage ? { xs: 0, md: 1 } : 0
          }}
        >
          <Box sx={{ width: '95%' }}>
            {isMobile ? (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr auto auto',
                  gridTemplateRows: 'auto auto',
                  columnGap: 1,
                  rowGap: 0.5,
                  width: '100%',
                  alignItems: 'center',
                  mt: 0,
                  mb: 0,
                  '& > .MuiStack-root': {
                    display: 'contents'
                  },
                  '& > .MuiStack-root > .MuiGrid-container': {
                    gridColumn: 2,
                    gridRow: 1,
                    width: 'auto',
                    alignItems: 'center',
                    minHeight: 'unset',
                    '& .MuiGrid-item': {
                      width: 'auto',
                      maxWidth: 'none',
                      flexBasis: 'auto',
                      paddingBottom: 0,
                      marginBottom: 0
                    },
                    '& .MuiBox-root': {
                      width: 'auto',
                      minWidth: 0,
                      minHeight: 'unset'
                    }
                  },
                  '& > .MuiStack-root > .MuiCollapse-root': {
                    gridColumn: '1 / -1',
                    gridRow: 2,
                    width: '100%'
                  }
                }}
              >
                <Filters filters={filterConfigs} />
                <Box
                  sx={{
                    gridColumn: 3,
                    gridRow: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5
                  }}
                >
                  {canManageBanners && (
                    <Tooltip title="הוסף/ערוך באנר">
                      <UiIconButton
                        color="primary"
                        variant="outlined"
                        shape="circle"
                        size="medium"
                        onClick={() => setOpenBannersDialog(true)}
                        aria-label="הוסף/ערוך באנר"
                      >
                        <Icon icon="image" color="inherit" />
                      </UiIconButton>
                    </Tooltip>
                  )}
                  {addPostIconButton}
                </Box>
              </Box>
            ) : (
              <Filters filters={filterConfigs} filtersAttachedButton={addPostTextButton} />
            )}
          </Box>
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              '& .infinite-scroll-component > div:first-of-type .MuiCard-root': {
                mt: { xs: 0, md: 3 }
              }
            }}
          >
            {loadingSearch && posts.length === 0 && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  py: 4,
                  flexDirection: 'column',
                  gap: 2
                }}
              >
                <Loader />
                <p style={{ textAlign: 'center', margin: 0 }}>
                  <b>טוען פוסטים...</b>
                </p>
              </Box>
            )}

            <InfiniteScroll
              scrollableTarget="scrollableDiv"
              dataLength={posts.length}
              next={fetchNextData}
              hasMore={hasMorePages}
              loader={
                <p style={{ textAlign: 'center' }}>
                  <b>טוען...</b>
                </p>
              }
            >
              {posts[0] && (
                <div key={posts[0].id} ref={el => (postRefs.current[posts[0].id] = el)}>
                  <FeedCard post={posts[0]} refreshFeed={refreshFeed} />
                </div>
              )}

              <FeedBannersCarousel refreshKey={bannersRefreshKey} />

              {posts[1] && (
                <div key={posts[1].id} ref={el => (postRefs.current[posts[1].id] = el)}>
                  <FeedCard post={posts[1]} refreshFeed={refreshFeed} />
                </div>
              )}

              {businessAds.length > 0 && <BusinessAdsCarousel ads={businessAds} />}

              {posts.slice(2).map((post: Post) => (
                <div key={post.id} ref={el => (postRefs.current[post.id] = el)}>
                  <FeedCard post={post} refreshFeed={refreshFeed} />
                </div>
              ))}
            </InfiniteScroll>
          </Box>
        </Box>
      </Box>

      {isMobile ? (
        <AddPostMobileSheet
          open={expanded}
          isDirty={isPostDirty}
          onClose={closeAddPostDialog}
          onDiscard={handleCleanPost}
          onPublish={addPost}
          onClean={handleCleanPost}
          publishLoading={loading}
          publishDisabled={editContent === `<p>${initialPostString}</p>`}
        >
          {addPostForm}
        </AddPostMobileSheet>
      ) : (
        <Dialog
          open={expanded}
          onClose={closeAddPostDialog}
          maxWidth="md"
          fullWidth
          disableEnforceFocus
          title="הוספת פוסט"
          secondaryActions={[{ label: 'ניקוי', onClick: handleCleanPost }]}
          primaryAction={{
            label: 'פרסם',
            onClick: addPost,
            loading,
            color: 'secondary',
            disabled: editContent === `<p>${initialPostString}</p>`
          }}
        >
          {addPostForm}
        </Dialog>
      )}

      {canManagePermissions && (
        <Dialog
          open={openTagsDialog}
          onClose={() => setOpenTagsDialog(false)}
          maxWidth="sm"
          fullWidth
          title="הגדרת הרשאות צפייה בפוסט"
        >
          <FeedTagsAllow onClose={() => setOpenTagsDialog(false)} existingTags={tagsAllow} onSave={handleSaveTags} />
        </Dialog>
      )}

      {canManageBanners && (
        <FeedBannersDialog
          open={openBannersDialog}
          onClose={() => setOpenBannersDialog(false)}
          onBannersChange={() => setBannersRefreshKey(prev => prev + 1)}
        />
      )}
    </>
  );
}
export default function Feed() {
  return <FeedContainer isPage={true} />;
}
